import React, { useState } from "react";
import { changePassword } from "../../services/changePasswordService";
import { toast } from "react-toastify";
import { Eye, EyeOff } from "lucide-react";
import { getErrorMessage } from "../../utils/getErrorMessage";

const Settings = () => {
  const [activeTab, setActiveTab] = useState("Security");
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [passwords, setPasswords] = useState({
    current_password: "",
    new_password: "",
    new_password_confirmation: "",
  });
  const [loading, setLoading] = useState(false);

  const tabs = ["Security"];

  const togglePassword = (field) => {
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleInputChange = (field, value) => {
    setPasswords((prev) => ({ ...prev, [field]: value }));
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await changePassword({
        current_password: passwords.current_password,
        new_password: passwords.new_password,
        new_password_confirmation: passwords.new_password_confirmation,
      });
      toast.success("Password changed successfully!");
      setPasswords({
        current_password: "",
        new_password: "",
        new_password_confirmation: "",
      });
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to change password"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white p-4 sm:p-6 md:p-8 font-sans text-slate-600">
      {/* --- HEADER --- */}
      <div className="mb-4 sm:mb-8">
        <h1 className="text-lg sm:text-xl font-bold text-black">Settings</h1>
        <p className="text-xs sm:text-sm text-slate-500 font-medium">
          Manage your store configuration and preferences
        </p>
      </div>

      {/* --- TABS --- */}
      <div className="flex gap-4 sm:gap-8 border-b border-[#ECEDEF] mb-4 sm:mb-8 overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`pb-2 sm:pb-3 text-xs sm:text-sm font-medium transition-all whitespace-nowrap active:scale-95 duration-100 ${
              activeTab === tab
                ? "text-[#6B7143] border-b-2 border-[#6B7143]"
                : "text-slate-400 hover:text-slate-600"
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* --- TAB CONTENT --- */}
      <div className="max-w-full sm:max-w-2xl">
        {activeTab === "Security" && (
          <div className="bg-white border border-[#ECEDEF] rounded-2xl p-4 sm:p-8 shadow-sm">
            <h2 className="text-base sm:text-lg font-bold text-black mb-4 sm:mb-6">
              Security
            </h2>
            <form
              className="space-y-4 sm:space-y-5"
              onSubmit={handleChangePassword}
            >
              <PasswordField
                label="Current password"
                value={passwords.current_password}
                onChange={(e) =>
                  handleInputChange("current_password", e.target.value)
                }
                visible={showPasswords.current}
                toggle={() => togglePassword("current")}
              />
              <PasswordField
                label="New Password"
                value={passwords.new_password}
                onChange={(e) =>
                  handleInputChange("new_password", e.target.value)
                }
                visible={showPasswords.new}
                toggle={() => togglePassword("new")}
              />
              <PasswordField
                label="Confirm Password"
                value={passwords.new_password_confirmation}
                onChange={(e) =>
                  handleInputChange("new_password_confirmation", e.target.value)
                }
                visible={showPasswords.confirm}
                toggle={() => togglePassword("confirm")}
              />
              <button
                type="submit"
                className="mt-2 sm:mt-4 bg-[#D9CBBF] hover:bg-[#c9b9ad] text-black px-4 sm:px-6 py-2 sm:py-2.5 rounded-xl font-bold text-xs sm:text-sm transition-colors active:scale-95 active:shadow-sm duration-100"
                disabled={loading}
              >
                {loading ? "Changing..." : "Change Password"}
              </button>
            </form>
          </div>
        )}

        {/* Tax settings tab and content removed */}
      </div>
    </div>
  );
};

// Helper Password Field Component
const PasswordField = ({ label, value, onChange, visible, toggle }) => (
  <div>
    <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1 sm:mb-2">
      {label}
    </label>
    <div className="relative">
      <input
        type={visible ? "text" : "password"}
        value={value}
        onChange={onChange}
        className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-1 focus:ring-[#6B7143] text-slate-600 tracking-widest text-xs sm:text-sm"
        autoComplete="off"
      />
      <button
        type="button"
        onClick={toggle}
        className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-500 transition-colors active:scale-90 duration-100"
      >
        {visible ? <EyeOff size={18} /> : <Eye size={18} />}
      </button>
    </div>
  </div>
);

export default Settings;
